/**
 * Recommended effective date + benefits recency hooks (PAC-021, ENR-024).
 *
 * Wraps the Cases service Employer-stage surfaces:
 *   - GET  /api/v1/cases/{id}/effective-date    → EffectiveDate (recommendation)
 *   - POST /api/v1/cases/{id}/effective-date     → EffectiveDate (staff override)
 *   - GET  /api/v1/cases/{id}/benefits-recency   → BenefitsRecency (validation)
 *
 * The recommended effective date is produced by the rules engine (versioned,
 * with a factor breakdown). Staff may override with a date + reason — the
 * override is the human-in-the-loop escape hatch and is audited server-side.
 *
 * Both degrade gracefully on 404 (`available = false`) so the page renders a
 * friendly disabled state when an endpoint is not yet implemented.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** A single factor behind the recommendation (`EffectiveDateFactor`). */
export interface EffectiveDateFactor {
  label: string;
  value: string;
}

/**
 * The recommended-effective-date card payload (rules-engine output).
 * Mirrors `services/cases` `EffectiveDateRecommendation`.
 */
export interface EffectiveDate {
  /** ISO date the rules engine recommends as the coverage effective date. */
  recommended_date: string | null;
  /** Version of the effective-date rule set that produced the recommendation. */
  rule_version: string | null;
  /** Factor breakdown (label/value pairs) that drove the recommendation. */
  factors: EffectiveDateFactor[];
  /** Staff override date, if one has been applied. */
  override_date?: string | null;
  /** Required reason for the override (audit). */
  override_reason?: string | null;
}

/** Inbound payload to override the recommended effective date (`{date, reason}`). */
export interface OverrideEffectiveDateBody {
  date: string;
  reason: string;
}

interface UseEffectiveDateResult {
  effectiveDate: EffectiveDate | null;
  /** False when the endpoint 404s (surface a disabled empty state). */
  available: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  override: (body: OverrideEffectiveDateBody) => Promise<boolean>;
}

/** Loads the recommended effective date and exposes the override action. */
export function useEffectiveDate(caseId: string | undefined): UseEffectiveDateResult {
  const [effectiveDate, setEffectiveDate] = useState<EffectiveDate | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<EffectiveDate>(`/cases/${encodeURIComponent(caseId)}/effective-date`)
      .then((data) => {
        setEffectiveDate({ ...data, factors: data.factors ?? [] });
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setAvailable(false);
          setEffectiveDate(null);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setEffectiveDate(null);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const override = useCallback(
    async (body: OverrideEffectiveDateBody): Promise<boolean> => {
      if (!caseId) return false;
      setIsWorking(true);
      setActionError(null);
      try {
        const data = await apiClient.post<EffectiveDate>(
          `/cases/${encodeURIComponent(caseId)}/effective-date`,
          body
        );
        if (data) setEffectiveDate({ ...data, factors: data.factors ?? [] });
        return true;
      } catch (err: unknown) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return false;
      } finally {
        setIsWorking(false);
      }
    },
    [caseId]
  );

  return {
    effectiveDate,
    available,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    override,
  };
}

/** Benefits-package recency validation payload (ENR-024). */
export interface BenefitsRecency {
  /** `current` | `stale` | `missing` — drives the recency badge color. */
  status: string;
  /** ISO timestamp the benefits package was last validated. */
  validated_at: string | null;
  /** Employer whose package was validated, if any. */
  employer_id?: string | null;
}

interface UseBenefitsRecencyResult {
  recency: BenefitsRecency | null;
  available: boolean;
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

/** Loads the benefits-package recency badge data for the Employer stage. */
export function useBenefitsRecency(caseId: string | undefined): UseBenefitsRecencyResult {
  const [recency, setRecency] = useState<BenefitsRecency | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<BenefitsRecency>(`/cases/${encodeURIComponent(caseId)}/benefits-recency`)
      .then((data) => {
        setRecency(data);
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setAvailable(false);
          setRecency(null);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setRecency(null);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { recency, available, isLoading, error, refresh };
}
